import { useState } from "react";
import { type ClienteNexo, type PorQueOfac as DatosPorQue, visorSnapshotUrl } from "../api.ts";
import { C, autoridadEs, fecha, st } from "../theme.ts";
import { ventanaTexto } from "../ventana-texto.ts";

const FUENTE_MENCION_ES: Record<string, string> = {
  us_doj_news: "Departamento de Justicia",
  us_treasury_ofac: "Departamento del Tesoro",
  us_fbi_wanted: "FBI",
  us_federal_register: "Federal Register",
  us_fincen: "Oficina del Tesoro contra el lavado",
};

/** Sólo enlaces https (revisión de #218: un href con otro esquema no se pinta). */
const seguro = (u: string | null | undefined): string | null => (u && /^https:\/\//.test(u) ? u : null);
const emisor = (id: string) => (id.startsWith("us_treasury_ofac:state:") ? "Departamento de Estado" : "Departamento del Tesoro");
const enlace = { color: C.tiel, textDecoration: "none", fontWeight: 600 } as const;
const pista = { fontSize: 12.5, color: C.muted, margin: "10px 0 4px" } as const;

/** ¿Hay algo que decir? Una entidad sin sanción ni comunicados no lleva la sección. */
/** Las medidas que explican una lista: OFAC y FinCEN (los avisos del SAT van en su propia sección). */
const medidas = (d: DatosPorQue) => d.programas.filter((p) => ["OFAC", "FINCEN"].includes((p.autoridad || "OFAC").toUpperCase()));
export function tienePorQue(d: DatosPorQue | null): d is DatosPorQue {
  return !!d && (medidas(d).length > 0 || d.comunicados_directos.length > 0 || d.comunicados_de_la_accion.length > 0 || d.organigramas.length > 0);
}

/**
 * Por qué la designó OFAC (Bernardo, 2026-10-01: arriba de la ficha). Los datos son de la sesión treasury
 * (public.nexo_viz_entidad_por_que): el programa en lenguaje llano, el pasaje del comunicado que la nombra, los comunicados
 * de la misma acción, los organigramas, las fotos cuyo pie de foto coincide con el nombre y las menciones en otros documentos,
 * que son coincidencias de nombre sin confirmar salvo que una persona las haya confirmado.
 */
export function PorQueOfac({ cliente, datos }: { cliente: ClienteNexo; datos: DatosPorQue }) {
  const [abriendo, setAbriendo] = useState<string | null>(null);
  const [errorArchivo, setErrorArchivo] = useState<string | null>(null);
  const abrir = async (documento: string) => {
    setAbriendo(documento);
    setErrorArchivo(null);
    try {
      window.open(await visorSnapshotUrl(cliente, documento), "_blank", "noopener");
    } catch (e: unknown) {
      setErrorArchivo(e instanceof Error ? e.message : String(e));
    } finally {
      setAbriendo(null);
    }
  };
  const otrasFotos = datos.totales.fotos_de_la_accion - datos.fotos.length;
  // Revisión de #218: una entidad sólo con medidas de FinCEN no está «designada por OFAC»; la fecha de alta de la base no
  // distingue autoridad, así que sólo se dice «en la lista de OFAC desde» cuando todas sus medidas son de OFAC.
  const progs = medidas(datos);
  const deOfac = progs.some((p) => (p.autoridad || "OFAC").toUpperCase() === "OFAC");
  const soloOfac = deOfac && progs.every((p) => (p.autoridad || "OFAC").toUpperCase() === "OFAC");
  return (
    <div style={{ ...st.tarjeta, marginTop: 12 }} id="por-que-ofac">
      <p style={st.eyebrow}>{deOfac || progs.length === 0 ? "Por qué la sancionó OFAC" : "Por qué la señaló la Oficina del Tesoro contra el lavado"}</p>
      {datos.fecha_alta_en_lista && soloOfac ? (
        <p style={{ fontSize: 13.5, margin: "0 0 8px" }}>En la lista de OFAC desde el {fecha(datos.fecha_alta_en_lista)}.</p>
      ) : null}
      {progs.length > 0 ? (
        <ul style={{ margin: "0 0 4px", paddingLeft: 18 }}>
          {progs.map((p, i) => (
            <li key={i} style={{ fontSize: 13.5, margin: "4px 0", lineHeight: 1.5 }}>
              {p.explicacion ?? "Programa sin explicación en el catálogo."}
              {p.estado_medida === "propuesta" ? <b style={{ color: C.amber }}> Propuesta: todavía no obliga.</b> : null}
              <span style={{ color: C.muted, fontSize: 12 }}> · {[autoridadEs(p.autoridad), p.programa, p.base_legal].filter(Boolean).join(" · ")}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {datos.comunicados_directos.map((c) => {
        const pasaje = ventanaTexto(c.fragmento.parrafo, c.fragmento.texto);
        return (
          <figure key={c.documento_id} style={{ margin: "10px 0" }}>
            {pasaje ? (
              <blockquote lang="en" style={{ margin: "0 0 4px", padding: "8px 12px", borderLeft: `3px solid ${C.red}`, background: C.grayBg, fontSize: 13, lineHeight: 1.55 }}>
                {pasaje}
              </blockquote>
            ) : null}
            <figcaption style={{ fontSize: 12.5, color: C.muted }}>
              Comunicado del {emisor(c.documento_id)}{c.fecha ? `, ${fecha(c.fecha)}` : ""}
              {c.rol !== "designado" ? " (la menciona)" : ""}:{" "}
              {seguro(c.url) ? <a href={seguro(c.url)!} target="_blank" rel="noreferrer" style={enlace}>{c.titulo}</a> : c.titulo}
              {pasaje ? " · en inglés, como se publicó" : ""}
            </figcaption>
          </figure>
        );
      })}
      {datos.comunicados_directos.length === 0 ? <p style={pista}>Nexo no tiene un comunicado que la nombre.</p> : null}

      {datos.comunicados_de_la_accion.length > 0 ? (
        <>
          <p style={pista}>Comunicados de la misma acción (no la nombran):</p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {datos.comunicados_de_la_accion.map((c) => (
              <li key={c.documento_id} style={{ fontSize: 13 }}>
                {seguro(c.url) ? <a href={seguro(c.url)!} target="_blank" rel="noreferrer" style={enlace}>{c.titulo}</a> : c.titulo}
                {c.fecha ? <span style={{ color: C.muted }}> · {fecha(c.fecha)}</span> : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {datos.organigramas.length > 0 ? (
        <>
          <p style={pista}>Organigramas de la red publicados por OFAC:</p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {datos.organigramas.map((o) => (
              <li key={o.documento_id} style={{ fontSize: 13 }}>
                {o.titulo}
                {o.fecha ? <span style={{ color: C.muted }}> · {fecha(o.fecha)}</span> : null}
                {o.tiene_archivo ? (
                  <button type="button" onClick={() => void abrir(o.documento_id)} disabled={abriendo === o.documento_id}
                    style={{ ...enlace, background: "none", border: "none", cursor: "pointer", padding: 0, marginLeft: 8, fontSize: 13 }}>
                    {abriendo === o.documento_id ? "Abriendo…" : "Abrir copia guardada"}
                  </button>
                ) : null}
                {seguro(o.url) ? <a href={seguro(o.url)!} target="_blank" rel="noreferrer" style={{ ...enlace, marginLeft: 8 }}>original</a> : null}
                {seguro(o.wayback_url) ? <a href={seguro(o.wayback_url)!} target="_blank" rel="noreferrer" style={{ ...enlace, marginLeft: 8 }}>archivo web</a> : null}
              </li>
            ))}
          </ul>
          {errorArchivo ? <p style={{ fontSize: 12.5, color: C.amber, margin: "4px 0 0" }}>No se pudo abrir la copia guardada ({errorArchivo}).</p> : null}
        </>
      ) : null}

      {datos.fotos.length > 0 ? (
        <>
          <p style={pista}>Fotos de la acción (coincidencia de nombre en el pie de foto):</p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {datos.fotos.map((f) => (
              <li key={f.documento_id} style={{ fontSize: 13 }}>
                {seguro(f.url) ? <a href={seguro(f.url)!} target="_blank" rel="noreferrer" style={enlace}>{f.pie ?? f.titulo}</a> : f.pie ?? f.titulo}
                {f.credito ? <span style={{ color: C.muted }}> · {f.credito}</span> : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {otrasFotos > 0 ? (
        <p style={{ ...pista, margin: "6px 0 0" }}>La acción publicó {String(otrasFotos)} {otrasFotos === 1 ? "foto más" : "fotos más"} cuyo pie de foto no la nombra.</p>
      ) : null}

      {datos.menciones.total > 0 ? (
        <>
          <p style={pista}>Otros documentos que la nombran ({String(datos.menciones.total)}). Coincidencia de nombre, sin confirmar salvo que se indique:</p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {datos.menciones.ejemplos.map((m) => (
              <li key={m.documento_id} style={{ fontSize: 13, margin: "3px 0" }}>
                {seguro(m.url) ? <a href={seguro(m.url)!} target="_blank" rel="noreferrer" style={enlace}>{m.titulo}</a> : m.titulo}
                <span style={{ color: C.muted }}>
                  {" "}· {FUENTE_MENCION_ES[m.fuente] ?? m.fuente}{m.fecha ? ` · ${fecha(m.fecha)}` : ""}
                  {m.estado === "confirmada" ? " · confirmada por una persona" : " · sin confirmar"}
                  {m.otros_candidatos > 0 ? ` · el nombre también coincide con ${String(m.otros_candidatos)} ${m.otros_candidatos === 1 ? "entidad más" : "entidades más"}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
